import {CreditCard, Savings} from "@mui/icons-material";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import {
	Box,
	Button,
	Card,
	CardActions,
	CardContent,
	CardHeader,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Stack,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import {ErrorAlert, Instant, Now, TemporalFormat, tryParseInstant, useFlag, View} from "@variocube/app-ui";
import React, {useEffect} from "react";
import {useAsync, useAsyncCallback} from "react-async-hook";
import {Link as RouterLink} from "react-router-dom";
import {useIndexesApi, useInvoicesApi} from "../api";
import {Infoboxes} from "../component/infoboxes";
import {isOebb, useInvoiceCount, useReloadCreditCards, useUser, useUserCreditCards} from "../context/auth";
import {useLocalization} from "../i18n";
import {getUserDisplayString} from "../util";
import {canRoute} from "../utils/canRoute";
import {formatCreditCardNumber} from "../utils/formatCreditCardNumber";
import {useCanAudit} from "../utils/useCanAudit";
import {getInvoiceDisplayString, getSubmitterName} from "./invoice/displays";
import {InvoiceTypeIcon} from "./invoice/invoice-type-icon";

export function Dashboard() {
	const {t} = useLocalization();
	const user = useUser();
	const creditCards = useUserCreditCards();
	const {mine, awaitingRouting, awaitingAudit} = useInvoiceCount() ?? {};
	const oebb = isOebb(user);
	const creditCard = oebb && creditCards.length > 0;
	const canRouteInvoices = canRoute(user);
	const canAuditInvoices = useCanAudit(user);
	const {verifyCurrentUserCreditCards} = useIndexesApi();
	const reloadCreditCards = useReloadCreditCards();

	const theme = useTheme();
	const lg = useMediaQuery(theme.breakpoints.up("lg"));

	const [verifyCreditCardDialogOpen, setVerifyCreditCardDialogOpen, clearVerifyCreditCardDialogOpen] = useFlag(false);

	useEffect(() => {
		const verify = creditCards.some(cc => {
			const lastVerified = tryParseInstant(cc.lastVerified);
			const reference = Now.instant().subtract({hours: 24 * 30});
			return !lastVerified || Instant.compare(lastVerified, reference) < 0;
		});
		if (verify) {
			setVerifyCreditCardDialogOpen();
		}
	}, [creditCards]);

	const verifyCreditCards = useAsyncCallback(async () => {
		await verifyCurrentUserCreditCards();
		await reloadCreditCards();
		clearVerifyCreditCardDialogOpen();
	});

	return (
		<View maxWidth="xl">
			<Typography variant="h1" gutterBottom>
				{t("dashboard.greeting", {name: user !== undefined ? getUserDisplayString(user) : ""})}
			</Typography>
			<Box>
				<Grid container spacing={2}>
					<Grid item xs={12} sm={6} md={4} lg={3}>
						<Stack spacing={2}>
							<Card>
								<CardHeader
									title={t(`dashboard.creditorInvoice.title.${oebb ? "oebb" : "creditor"}`)}
								/>
								<CardContent>
									<Typography>
										{t(`dashboard.creditorInvoice.message.${oebb ? "oebb" : "creditor"}`)}
									</Typography>
								</CardContent>
								<CardContent>
									<Button
										variant="contained"
										component={RouterLink}
										to="/invoices/new/creditor"
										startIcon={<DocumentScannerIcon />}
										fullWidth
									>
										{t(`dashboard.creditorInvoice.title.${oebb ? "oebb" : "creditor"}`)}
									</Button>
								</CardContent>
							</Card>
							{oebb && (
								<Card>
									<CardHeader title={t(`dashboard.expenseInvoice.title`)} />
									<CardContent>
										<Typography>{t(`dashboard.expenseInvoice.message`)}</Typography>
									</CardContent>
									<CardContent>
										<Button
											variant="contained"
											component={RouterLink}
											to="/invoices/new/expense"
											startIcon={<Savings />}
											fullWidth
										>
											{t(`dashboard.expenseInvoice.title`)}
										</Button>
									</CardContent>
								</Card>
							)}
							{creditCard && (
								<Card>
									<CardHeader title={t(`dashboard.creditCardInvoice.title`)} />
									<CardContent>
										<Typography>{t(`dashboard.creditCardInvoice.message`)}</Typography>
									</CardContent>
									<CardContent>
										<Button
											variant="contained"
											component={RouterLink}
											to="/invoices/new/credit-card"
											startIcon={<CreditCard />}
											fullWidth
										>
											{t(`dashboard.creditCardInvoice.title`)}
										</Button>
									</CardContent>
								</Card>
							)}
						</Stack>
					</Grid>

					<Grid item xs={12} sm={6} md={4} lg={3}>
						<Stack spacing={2}>
							<Card>
								<CardContent sx={{pb: 0}}>
									<Typography variant="overline">
										{t("invoices.invoiceViews.Mine")}
									</Typography>
									<Typography variant="h2" component="div">
										{mine}
									</Typography>
								</CardContent>
								<CardActions>
									<Button
										color="secondary"
										component={RouterLink}
										to="/invoices?view=Mine&reset=true"
									>
										{t("navigation.myInvoices")}
									</Button>
								</CardActions>
							</Card>
							{canRouteInvoices && (
								<Card sx={{height: "100%"}}>
									<CardContent sx={{pb: 0}}>
										<Typography variant="overline">
											{t("invoices.invoiceViews.AwaitingRouting")}
										</Typography>
										<Typography variant="h2" component="div">
											{awaitingRouting}
										</Typography>
									</CardContent>
									<CardActions>
										<Button
											color="secondary"
											component={RouterLink}
											to="/invoices?view=AwaitingRouting&reset=true"
										>
											{t("navigation.pendingInvoices")}
										</Button>
									</CardActions>
								</Card>
							)}
							{canAuditInvoices && (
								<Card>
									<CardContent sx={{pb: 0}}>
										<Typography variant="overline">
											{t("invoices.invoiceViews.AwaitingAudit")}
										</Typography>
										<Typography variant="h2" component="div">
											{awaitingAudit}
										</Typography>
									</CardContent>

									<CardActions>
										<Button
											color="secondary"
											component={RouterLink}
											to="/invoices?view=AwaitingAudit&reset=true"
										>
											{t("navigation.auditingInvoices")}
										</Button>
									</CardActions>
								</Card>
							)}
							<AuditQueueCard />
						</Stack>
					</Grid>
					<Grid item xs={12} sm={6} md={4} lg={6}>
						<Infoboxes displayType="Dashboard" columns={lg ? 2 : 1} />
					</Grid>
				</Grid>
			</Box>
			<Dialog open={verifyCreditCardDialogOpen} onClose={clearVerifyCreditCardDialogOpen}>
				<DialogTitle>{t("dashboard.verifyCreditCard.title")}</DialogTitle>
				<DialogContent>
					{t("dashboard.verifyCreditCard.message")}
				</DialogContent>
				<List>
					{creditCards.map(cc => (
						<ListItem key={cc.id}>
							<ListItemIcon>
								<CreditCard />
							</ListItemIcon>
							<ListItemText
								primary={formatCreditCardNumber(cc.cardNumber)}
								secondary={cc.creditor.companyName}
							/>
						</ListItem>
					))}
				</List>
				<ErrorAlert error={verifyCreditCards.error} />
				<DialogActions>
					<Button disabled={verifyCreditCards.loading} onClick={verifyCreditCards.execute}>
						{t("dashboard.verifyCreditCard.yes")}
					</Button>
					<Button disabled={verifyCreditCards.loading} component={RouterLink} to="/user-settings">
						{t("dashboard.verifyCreditCard.no")}
					</Button>
				</DialogActions>
			</Dialog>
		</View>
	);
}

function AuditQueueCard() {
	const {t} = useLocalization();
	const {listInvoices} = useInvoicesApi();
	const user = useUser();
	const canAuditInvoices = useCanAudit(user);

	// Fetch invoices awaiting audit for the audit queue card
	// Filter by assignedToMeAsAuditor to only show invoices assigned to the current user
	const {result: auditQueueInvoices} = useAsync(
		async () => {
			if (!canAuditInvoices) return null;
			return listInvoices({awaitingAudit: true, assignedToMeAsAuditor: true, size: 5, page: 0});
		},
		[canAuditInvoices],
	);

	if (!canAuditInvoices) {
		return null;
	}

	return (
		<Card>
			<CardHeader title={t("dashboard.auditQueue.title")} />
			{auditQueueInvoices?.content && auditQueueInvoices.content.length > 0
				? (
					<List>
						{auditQueueInvoices.content.map(invoice => {
							const displayString = getInvoiceDisplayString(invoice);
							const submitterName = getSubmitterName(invoice);
							const amount = invoice.gross ? `€${invoice.gross.toFixed(2)}` : "";

							return (
								<ListItemButton
									key={invoice.id}
									component={RouterLink}
									to={`/invoices/${invoice.id}`}
								>
									<ListItemIcon>
										<InvoiceTypeIcon invoiceType={invoice.invoiceType} />
									</ListItemIcon>
									<ListItemText
										primary={displayString}
										secondary={
											<Stack>
												<Typography variant="body2" color="textSecondary">
													{submitterName}
													{amount ? ` • ${amount}` : ""}
												</Typography>
												<Typography variant="body2" color="textSecondary">
													<TemporalFormat
														value={tryParseInstant(invoice.submitted)}
													/>
												</Typography>
											</Stack>
										}
										primaryTypographyProps={{
											sx: {
												overflow: "hidden",
												textOverflow: "ellipsis",
												display: "-webkit-box",
												WebkitLineClamp: 3,
												WebkitBoxOrient: "vertical",
											},
										}}
									/>
								</ListItemButton>
							);
						})}
					</List>
				)
				: (
					<CardContent>
						<Typography color="text.secondary">
							{t("dashboard.auditQueue.emptyMessage")}
						</Typography>
					</CardContent>
				)}
		</Card>
	);
}
